import { Col, Form, message, Row, Spin, Modal } from "antd"
import { useEffect, useState, useCallback } from "react"
import { useQueryClient } from '@tanstack/react-query'
import BlogHeader from "./BlogHeader"
import ExcerptCard from "./ExcerptCard"
import VisibilityCard from "./VisibilityCard"
import FeaturedImageCard from "./FeaturedImageCard"
import ShareSettingsCard from "./ShareSettingsCard"
import DeleteAction from "./DeleteAction"
import { PostDetailsCard } from "./PostDetailsCard"
import BlogPreview from "./BlogPreview"
import { useCreateBlog, useUpdateBlog, getBlogDetailsById, useDeleteBlog } from "api/Blogs/Blogs"
import { Visibility } from "types/blogs/blogs"
import { useQuery } from '@tanstack/react-query'
import Loader from "helper/Loader"
import moment from "moment"
import { infoMessage } from "api/global"

interface CreateBlogPostProps {
  onSuccess?: () => void
  blogId?: string
  isEditMode?: boolean
}

export const CreateBlogPost = ({ onSuccess, blogId, isEditMode = false }: CreateBlogPostProps) => {
  const [form] = Form.useForm()
  const [publishStatus, setPublishStatus] = useState<Visibility>(Visibility.PUBLIC)
  const [content, setContent] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [formData, setFormData] = useState<any>({})
  const [currentOrganization, setCurrentOrganization] = useState<any>(null)
  const [currentOrgOrganizers, setCurrentOrgOrganizers] = useState<any[]>([])
  const [avatar, setAvatar] = useState<string>()
  const [isDefaultOrganizer, setIsDefaultOrganizer] = useState<boolean>(true)
  const [defaultOrganizer, setDefaultOrganizer] = useState(null)
  const queryClient = useQueryClient()
  const { mutate: createBlogPost, isLoading: creating } = useCreateBlog()
  const { mutate: updateBlogPost, isLoading: updating } = useUpdateBlog()
  const { mutate: deleteBlogPost, isLoading: deleting } = useDeleteBlog()
  const isSaving = creating || updating || deleting

  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    const hasTitle = formData?.title && formData.title.trim().length > 0
    const hasContent = content && content.trim().length >= 20
    const hasOrganization = formData?.organizationId || currentOrganization?.id
    const hasAuthors = formData?.authorIds && formData.authorIds.length > 0
    const hasVisibility = formData?.visibility

    return hasTitle || hasContent || hasOrganization || hasAuthors || hasVisibility
  }

  // Clear formData when starting new blog creation (not edit mode)
  useEffect(() => {
    if (!isEditMode && !blogId) {
      setFormData({})
      setContent('')
      setAvatar(undefined)
      setCurrentOrganization(null)
      setCurrentOrgOrganizers([])
      form.resetFields()
      setIsDefaultOrganizer(true)
      setDefaultOrganizer(null)
    }
  }, [isEditMode, blogId, form])

  // Fetch blog data when in edit mode
  const { data: BlogDetails, isLoading: blogLoading, refetch: refetchBlogDetails } = useQuery({
    queryKey: ['blog-details', blogId],
    queryFn: () => getBlogDetailsById(blogId),
    enabled: isEditMode,
  })

  // Populate form with blog data when in edit mode
  useEffect(() => {
    if (!BlogDetails) return
    if (isEditMode && BlogDetails && !blogLoading) {
      if (BlogDetails.organization)
        form.setFieldsValue({
          title: BlogDetails.title || '',
          excerpt: BlogDetails.excerpt,
          visibility: BlogDetails.visibility,
          photoId: BlogDetails.featureImageId,
          authorIds: BlogDetails.authors?.map((a: any) => a.id) || [],
          content: BlogDetails.content || '',
          organizationId: BlogDetails.organizationId,
          scheduledAt: BlogDetails.scheduledAt || null,
          sharingOptions: BlogDetails.sharingOptions,
        })
    }


    setFormData({
      ...BlogDetails,
      author: BlogDetails.authors?.map((a: any) => `${a.firstName} ${a.lastName}`).join(', '),
      authorAvatar: BlogDetails.authors?.length > 0 ? BlogDetails.authors[0].photo?.post : null,
      content: BlogDetails.content || '',
      organization: BlogDetails.organization?.name,
      featuredImage: BlogDetails.featureImage || null,
      publishDate: BlogDetails.scheduledAt
        ? new Date(BlogDetails.scheduledAt).toLocaleDateString()
        : new Date().toLocaleDateString(),
      // readTime: Math.ceil((BlogDetails.content || '').split(' ').length / 200) || 5,
    })

    // Set avatar for featured image
    if (BlogDetails?.featureImage?.original) {
      setAvatar(BlogDetails.featureImage.original)
    }
  }, [BlogDetails, isEditMode, blogLoading])


  // Handle form value changes
  const handleFormChange = (_: any, allValues: any) => {
    setFormData(prev => ({
      ...prev,
      ...allValues,
      content,
      organization: currentOrganization?.name || prev.organization, // Will be updated by currentOrganization effect
      featuredImage: avatar ? { id: allValues.photoId, original: avatar } : prev.featuredImage, // Update featured image when avatar changes
      publishDate: allValues.scheduledAt
        ? new Date(allValues.scheduledAt).toLocaleDateString()
        : prev.publishDate, // Preserve existing publish date if no new one
      // readTime: Math.ceil((content || '').split(' ').length / 200) || 5,
      authorIds: allValues.authorIds || prev.authorIds, // Preserve authorIds
    }))
  }


  // Handle authors change from PostDetailsCard
  const handleAuthorsChange = (authors: any[]) => {
    const authorNames = authors
      .map(a => `${a.firstName} ${a.lastName}`)
      .join(', ')

    const firstAuthorAvatar = authors.length > 0 ? authors[0].avatar : null
    setFormData(prev => ({
      ...prev,
      author: authorNames,
      authorIds: authors.map(a => a.id),
      authorAvatar: firstAuthorAvatar,
      organization: currentOrganization?.name || prev.organization
    }))
  }

  // Update formData when content changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      content,
      // readTime: Math.ceil((content || '').split(' ').length / 200) || 5
    }))
  }, [content])

  // Update formData when avatar changes
  useEffect(() => {
    if (avatar) {
      setFormData(prev => ({
        ...prev,
        featuredImage: { id: prev.featuredImage?.id || form.getFieldValue('photoId'), original: avatar }
      }))
    }
  }, [avatar])

  // Update organization name in formData when currentOrganization changes
  useEffect(() => {
    if (currentOrganization?.name) {
      setFormData(prev => ({
        ...prev,
        organization: currentOrganization.name
      }))
    }
  }, [currentOrganization])

  const getInitialDateTime = () => {
    if (BlogDetails?.scheduledAt) {
      const scheduledDate = moment(BlogDetails?.scheduledAt)
      return {
        date: scheduledDate,
        time: scheduledDate
      }
    }
    return { date: null, time: null }
  }

  const initialDateTime = getInitialDateTime()


  const handlePreviewChange = (show: boolean) => {
    setShowPreview(show)
  }

  const handleDelete = () => {
    Modal.confirm({
      title: 'Are you sure you want to delete this blog post?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        if (blogId) {
          deleteBlogPost(blogId, {
            onSuccess: () => {
              queryClient.invalidateQueries(['all-blogs'])
              onSuccess?.()
            },
            onError: () => {
              message.error('Failed to delete blog post')
            }
          })
        }
      }
    })
  }

  const handleSave = () => {
    form.validateFields()
      .then((values) => {
        const visibilityType = values.visibility ?? formData.visibility;

        // scheduledVisibility is set to PUBLIC only if scheduledAt is provided
        const scheduledVisibility =
          values.scheduledAt && visibilityType === Visibility.DRAFT
            ? Visibility.PUBLIC
            : values.scheduledVisibility ?? formData.scheduledVisibility;

        // Only set scheduledAt when visibility is DRAFT and scheduledVisibility is PUBLIC
        let scheduledAtUtc: string | undefined = undefined;
        if (
          values.scheduledAt &&
          visibilityType === Visibility.DRAFT &&
          scheduledVisibility === Visibility.PUBLIC
        ) {
          scheduledAtUtc = moment(values.scheduledAt).utc().toISOString();
        }
        if (content.length < 20) {
          infoMessage('Content must be at least 20 characters long')
          return
        }
        if (!visibilityType) {
          infoMessage('Please select a visibility')
          return
        }
        if (scheduledAtUtc) {
          const scheduledDate = new Date(scheduledAtUtc);

          if (scheduledDate <= new Date()) {
            infoMessage("Please select a future date and time");
            return;
          }
        }
        

        const blogData = {
          title: values.title ?? formData.title,
          excerpt: values.excerpt ?? "",
          content,
          visibility: visibilityType,
          scheduledVisibility,
          featureImageId: values.photoId || formData.featuredImage?.id,
          authorIds: values.authorIds?.length ? values.authorIds : formData.authorIds || [],
          organizationId: form.getFieldValue('organizationId'),
          // readingTime: Math.ceil((content || '').split(' ').length / 200),
          scheduledAt: scheduledAtUtc,
          sharingOptions: values.sharingOptions ?? formData.sharingOptions,
        }

        if (isEditMode && blogId) {
          updateBlogPost({ blogId, payload: blogData }, {
            onSuccess: () => {
              onSuccess?.()
              queryClient.invalidateQueries(['all-blogs'])
              queryClient.invalidateQueries(['blog-details', blogId])
              refetchBlogDetails() // Explicitly refetch to get latest data
            }
          })
        } else {
          createBlogPost(blogData, {
            onSuccess: (newBlog) => {
              onSuccess?.()
              queryClient.setQueryData(['all-blogs'], (oldData: any) => ({
                ...oldData,
                records: [newBlog, ...(oldData?.records || [])]
              }))
            }
          })
        }
      })
      .catch(err => console.error(err))
  }
  if (isEditMode && blogLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#fff' }}>
        <Loader />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#fff', minHeight: '100vh', position: 'relative' }}>
      {showPreview && (
        <BlogPreview
          visible={true}
          formData={formData}
          onEdit={() => handlePreviewChange(false)}
          onSave={handleSave}
          isSaving={isSaving}
          onBackToBlog={onSuccess}
        />
      )}
      <div style={{ display: showPreview ? 'none' : 'block' }}>
        {isSaving && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 9999,
          }}>
            <Loader />
          </div>
        )}

        <BlogHeader onEdit={isEditMode} onPreviewChange={handlePreviewChange} onSave={handleSave} onBackToBlog={onSuccess} isFormValid={isFormValid()} />
        <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
          <Row gutter={24}>
            {/* LEFT */}
            <Col xs={24} lg={16}>
              <PostDetailsCard
                form={form}
                content={content}
                setContent={setContent}
                BlogDetails={BlogDetails}
                onAuthorsChange={handleAuthorsChange}
                currentOrganization={currentOrganization}
                setCurrentOrganization={setCurrentOrganization}
                isEditMode={isEditMode}
              />
              <ExcerptCard
                value={form.getFieldValue('excerpt')}
                onChange={(value) => form.setFieldsValue({ excerpt: value })}
              />
            </Col>

            {/* RIGHT */}
            <Col xs={24} lg={8}>
              <VisibilityCard
                publishStatus={publishStatus}
                setPublishStatus={setPublishStatus}
                form={form}
                initialDateTime={initialDateTime}
              />
              <FeaturedImageCard form={form} BlogDetails={BlogDetails} avatar={avatar} setAvatar={setAvatar} />
              <ShareSettingsCard />
              {isEditMode && BlogDetails?.visibility !== Visibility.ARCHIVED && <DeleteAction onDelete={handleDelete} />}
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  )
}

export default CreateBlogPost
